import { useState, useEffect } from 'react';
import {
  getAllFunds, getStreamsByFund,
  saveRubric, publishRubric, getAllRubrics,
} from '../../../services/firestoreService';
import { useAuth } from '../../../hooks/useAuth';
import FormField from '../../shared/Forms/FormField';
import SelectField from '../../shared/Forms/SelectField';
import Button from '../../shared/UI/Button';
import QuestionBuilder from './QuestionBuilder';

// Formula types whose inputs live in cagr_config.year_question_ids rather than
// formula_inputs. Both dependency detection and validation must read the right
// field, or the new type's inputs are invisible to them. CAGR_USD_FUTURE shares
// the same config shape (its slots point at forecast questions).
const CAGR_TYPES = ['CAGR', 'CAGR_USD', 'CAGR_USD_FUTURE'];
const isCagrType = t => CAGR_TYPES.includes(t);

// Fix 1: derived questions may reference other derived questions as inputs.
// Chaining is allowed unless it forms a cycle (A depends on B which depends
// on A). This detects any cycle in the derived-question dependency graph.
function hasCyclicDependency(questions) {
  const graph = {};
  (questions || []).forEach(q => {
    if (!q.is_derived) return;
    graph[q.question_id] = [];
    if (isCagrType(q.formula_type)) {
      (q.cagr_config?.year_question_ids || []).forEach(id => {
        if (id) graph[q.question_id].push(id);
      });
    } else {
      Object.values(q.formula_inputs || {}).forEach(input => {
        const refs = Array.isArray(input) ? input : [input];
        refs.forEach(ref => {
          if (ref?.type === 'question' && ref?.question_id) {
            graph[q.question_id].push(ref.question_id);
          }
        });
      });
    }
  });

  const visited = new Set();
  const inStack = new Set();

  function dfs(nodeId) {
    if (inStack.has(nodeId)) return true; // cycle
    if (visited.has(nodeId)) return false;
    visited.add(nodeId);
    inStack.add(nodeId);
    for (const dep of (graph[nodeId] || [])) {
      if (dfs(dep)) return true;
    }
    inStack.delete(nodeId);
    return false;
  }

  return Object.keys(graph).some(id => dfs(id));
}

export default function RubricForm({ rubric, isNewVersion = false, onSaved, onCancel, onPreview }) {
  const { user } = useAuth();
  const isReadOnly = !!(rubric?.is_published && !isNewVersion);

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => {
    if (isNewVersion && rubric) {
      return {
        ...rubric,
        id: null,
        is_published: false,
        version: (rubric.version || 1) + 1,
        version_history: [],
        published_at: null,
        published_by: null,
      };
    }
    return rubric
      ? { ...rubric }
      : { name: '', fund_id: '', stream_id: null, normalisation_factor: 10, advancement_threshold: 5, questions: [] };
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [funds, setFunds] = useState([]);
  const [streams, setStreams] = useState([]);
  const [allRubrics, setAllRubrics] = useState([]);
  const [publishedWarning, setPublishedWarning] = useState('');

  useEffect(() => {
    getAllFunds().then(list => setFunds(list.filter(f => f.is_active !== false)));
    getAllRubrics().then(setAllRubrics);
  }, []);

  useEffect(() => {
    if (!form.fund_id) { setStreams([]); return; }
    getStreamsByFund(form.fund_id).then(setStreams);
  }, [form.fund_id]);

  // Warn if a published rubric already exists for this fund/stream combination
  useEffect(() => {
    if (isReadOnly || !form.fund_id || !allRubrics.length) {
      setPublishedWarning('');
      return;
    }
    const existingPublished = allRubrics.find(r =>
      r.fund_id === form.fund_id &&
      (r.stream_id || null) === (form.stream_id || null) &&
      r.is_published === true &&
      r.id !== form.id
    );
    if (existingPublished) {
      setPublishedWarning(
        `A published rubric already exists for this fund/stream. ` +
        `Creating a new rubric will not unpublish the existing one. ` +
        `Use "New Version" on the existing rubric instead of creating a new one.`
      );
    } else {
      setPublishedWarning('');
    }
  }, [form.fund_id, form.stream_id, allRubrics, form.id, isReadOnly]);

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  }

  function validateStep1() {
    const e = {};
    if (!form.name?.trim()) e.name = 'Rubric name is required';
    if (!form.fund_id) e.fund_id = 'Fund is required';
    if (!form.normalisation_factor) e.normalisation_factor = 'Required';
    if (form.advancement_threshold === '' || form.advancement_threshold === undefined) e.advancement_threshold = 'Required';
    return e;
  }

  function validatePublish() {
    const e = {};
    if (!form.questions?.length) { e._publish = 'At least one question is required to publish'; return e; }
    form.questions.forEach((q, i) => {
      if (!q.question_text?.trim()) e[`q_${i}_text`] = 'Required';
      if (q.weight === '' || q.weight === undefined) e[`q_${i}_weight`] = 'Required';
      if (q.response_type === 'figure' && (q.figure_score_bands || []).length > 1) {
        for (let b = 0; b < q.figure_score_bands.length - 1; b++) {
          if (q.figure_score_bands[b].max > q.figure_score_bands[b + 1].min) {
            e[`q_${i}_bands`] = 'Score bands must not overlap';
          }
        }
      }
      if (q.response_type === 'derived') {
        if (!q.formula_type) {
          e[`q_${i}_derived`] = 'Select a formula type';
        } else if (isCagrType(q.formula_type)) {
          const cfg = q.cagr_config || {};
          if (!cfg.min_periods || cfg.min_periods < 2) {
            e[`q_${i}_derived`] = 'Minimum years required must be at least 2';
          } else if (cfg.max_periods <= cfg.min_periods) {
            e[`q_${i}_derived`] = 'Max years must be greater than min years';
          } else {
            const filledYears = (cfg.year_question_ids || []).filter(Boolean).length;
            if (filledYears < cfg.min_periods) {
              e[`q_${i}_derived`] = `Select at least ${cfg.min_periods} year questions`;
            }
          }
        } else {
          // Check all required inputs are filled. Fix 1: derived inputs are
          // now allowed (chaining). Cycles are caught by the graph check below.
          const inputs = q.formula_inputs || {};
          const inputVals = Object.values(inputs).flat();
          const missing = inputVals.filter(v => v.type === 'question' && !v.question_id);
          if (Object.keys(inputs).length === 0) {
            e[`q_${i}_derived`] = 'Configure all required formula inputs';
          } else if (missing.length > 0) {
            e[`q_${i}_derived`] = 'All formula inputs must be selected';
          }
          // GENERAL needs Field A. Field B is optional (blank = single-term),
          // and the operator is only required once Field B is chosen.
          // Coefficients are optional (blank = 1) but must be finite when set.
          if (!e[`q_${i}_derived`] && q.formula_type === 'GENERAL') {
            const badCoeff = ['coeff_a', 'coeff_b'].some(p => {
              const ref = inputs[p];
              if (!ref) return false;
              return ref.value === '' || ref.value == null ||
                !isFinite(Number(ref.value));
            });
            if (!inputs.field_a) {
              e[`q_${i}_derived`] = 'Select Field A';
            } else if (inputs.field_b && !q.general_config?.operator) {
              e[`q_${i}_derived`] = 'Select an operator (+ − × ÷)';
            } else if (badCoeff) {
              e[`q_${i}_derived`] = 'Coefficients must be numbers (leave blank for 1)';
            }
          }
        }
        if (!e[`q_${i}_derived`] && (q.figure_score_bands || []).length === 0) {
          e[`q_${i}_bands`] = 'At least one score band is required';
        }
      }
    });

    // Fix 1: block only genuine circular dependencies between derived questions
    if (hasCyclicDependency(form.questions)) {
      e._publish = 'Circular dependency detected in derived questions. ' +
        'A derived question cannot depend on another derived question that ' +
        'depends on it.';
    }
    return e;
  }

  async function saveDraft() {
    const e1 = validateStep1();
    if (Object.keys(e1).length) { setErrors(e1); setStep(1); return; }
    setSaving(true);
    try {
      const id = await saveRubric({
        rubricId: isNewVersion ? null : (form.id || null),
        rubricData: { ...form },
        performedBy: user.uid,
      });
      set('id', id);
      setSuccessMsg('Saved as draft.');
      setTimeout(() => setSuccessMsg(''), 2500);
    } catch (err) {
      setErrors({ _global: err.message });
    } finally {
      setSaving(false);
    }
  }

  async function publish() {
    const allErrors = { ...validateStep1(), ...validatePublish() };
    if (Object.keys(allErrors).length) {
      setErrors(allErrors);
      if (allErrors.name || allErrors.fund_id || allErrors.normalisation_factor || allErrors.advancement_threshold) setStep(1);
      return;
    }
    setPublishing(true);
    try {
      let id = form.id;
      if (!id) {
        id = await saveRubric({ rubricId: null, rubricData: { ...form }, performedBy: user.uid });
      }
      await publishRubric({ rubricId: id, publishedBy: user.uid });
      setSuccessMsg('Rubric published and locked.');
      setTimeout(() => { setSuccessMsg(''); onSaved(); }, 2000);
    } catch (err) {
      setErrors({ _global: err.message });
    } finally {
      setPublishing(false);
    }
  }

  const fundOptions = funds.map(f => ({ value: f.id, label: f.name }));
  const streamOptions = [
    { value: '', label: 'Fund Level — No Stream' },
    ...streams.map(s => ({ value: s.id, label: s.name })),
  ];

  return (
    <div>
      {isReadOnly && (
        <div className="alert alert-info mb-4">
          This rubric is published and locked. Use "New Version" to create an editable copy.
        </div>
      )}
      {errors._global && <div className="alert alert-error">{errors._global}</div>}
      {successMsg && <div className="alert alert-success">{successMsg}</div>}
      {publishedWarning && (
        <div className="alert alert-warning mb-4">⚠ {publishedWarning}</div>
      )}

      <div className="stepper mb-4">
        <div className={`step ${step === 1 ? 'active' : 'completed'}`} onClick={() => setStep(1)} style={{ cursor: 'pointer' }}>
          1. Basic Details
        </div>
        <div className={`step ${step === 2 ? 'active' : ''}`} onClick={() => setStep(2)} style={{ cursor: 'pointer' }}>
          2. Questions ({(form.questions || []).length})
        </div>
      </div>

      {step === 1 && (
        <div className="card">
          <div className="card-header"><h2>Basic Details</h2></div>
          <div className="card-body">
            <div className="form-grid">
              <FormField label="Rubric Name" required error={errors.name} className="form-full">
                <input className={`form-input ${errors.name ? 'error' : ''}`}
                  value={form.name} onChange={e => set('name', e.target.value)} readOnly={isReadOnly} />
              </FormField>
              <SelectField label="Fund" required error={errors.fund_id}
                value={form.fund_id} onChange={e => set('fund_id', e.target.value)}
                options={fundOptions} disabled={isReadOnly} />
              <SelectField label="Stream" value={form.stream_id || ''}
                onChange={e => set('stream_id', e.target.value || null)}
                options={streamOptions} disabled={isReadOnly} />
              <FormField label="Normalisation Factor" required error={errors.normalisation_factor}
                hint="Weighted score is divided by this number">
                <input type="number" min="1"
                  className={`form-input ${errors.normalisation_factor ? 'error' : ''}`}
                  value={form.normalisation_factor}
                  onChange={e => set('normalisation_factor', parseFloat(e.target.value) || '')}
                  readOnly={isReadOnly} />
              </FormField>
              <FormField label="Advancement Threshold" required error={errors.advancement_threshold}
                hint="Minimum normalised score required to advance">
                <input type="number" min="0"
                  className={`form-input ${errors.advancement_threshold ? 'error' : ''}`}
                  value={form.advancement_threshold}
                  onChange={e => set('advancement_threshold', parseFloat(e.target.value) || '')}
                  readOnly={isReadOnly} />
              </FormField>
            </div>
          </div>
          <div className="card-footer">
            <Button variant="secondary" onClick={onCancel}>Cancel</Button>
            <Button variant="primary" onClick={() => {
              const e = validateStep1();
              if (Object.keys(e).length) { setErrors(e); return; }
              setStep(2);
            }}>
              Next: Questions →
            </Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="card">
          <div className="card-header">
            <h2>Questions</h2>
            {errors._publish && <span className="text-error" style={{ fontSize: 13 }}>{errors._publish}</span>}
          </div>
          <div className="card-body">
            {isReadOnly
              ? (form.questions || []).map((q, i) => (
                <div key={q.question_id} className="rubric-preview-question">
                  <h4>{i + 1}. {q.question_text}</h4>
                  <p className="text-muted" style={{ fontSize: 12 }}>{q.response_type} · Weight: {q.weight}</p>
                </div>
              ))
              : <QuestionBuilder questions={form.questions || []} onChange={q => set('questions', q)} errors={errors} showScores />
            }
          </div>
          <div className="card-footer">
            <Button variant="secondary" onClick={() => setStep(1)}>← Back</Button>
            <Button variant="ghost" onClick={() => onPreview(form)}>Preview</Button>
            {!isReadOnly && (
              <>
                <Button variant="secondary" loading={saving} onClick={saveDraft}>Save as Draft</Button>
                <Button variant="primary" loading={publishing} onClick={publish}>Publish Rubric</Button>
              </>
            )}
            {isReadOnly && <Button variant="secondary" onClick={onCancel}>Close</Button>}
          </div>
        </div>
      )}
    </div>
  );
}
